import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueueConfig } from "../hooks/useQueueConfig";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ColorPickerField } from "../components/ColorPickerField";
import { ZoneBgEditor } from "../components/ZoneBgEditor";
import { CompanyUsers } from "../components/CompanyUsers";
import { PlaylistEditor } from "../components/PlaylistEditor";
import { LayoutMiniature, LayoutPreview } from "../components/LayoutMiniature";
import { SubscriptionTab } from "../components/SubscriptionTab";
import { MEDIA_TYPES, LAYOUT_MODES, LAYOUT_LABELS, TTS_LANGUAGES, DEFAULT_CONFIG } from "../lib/constants";
import { useSpeechVoices } from "../lib/useSpeechVoices";
import { listVoicesForUI, pickVoice } from "../lib/tts";
import { toast } from "sonner";
import { LogOut, Tv, UserCog, Trash2, Plus, Volume2, RotateCw } from "lucide-react";

const Admin = () => {
  const navigate = useNavigate();
  const { logout, company } = useAuth();
  const { config, setConfig } = useQueueConfig();
  const allVoices = useSpeechVoices();
  const [planLimits, setPlanLimits] = useState({ custom_layout: false });
  const [newSlide, setNewSlide] = useState("");
  const [newPrefix, setNewPrefix] = useState("");
  const [newDesk, setNewDesk] = useState("");

  useEffect(() => {
    api
      .get("/plan/limits")
      .then(({ data }) => setPlanLimits(data.limits || {}))
      .catch(() => {});
  }, []);

  const update = (patch) => setConfig((prev) => ({ ...prev, ...patch }));
  const updateNested = (key, patch) =>
    setConfig((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const onLogout = () => {
    logout();
    navigate("/login");
  };

  const testTTS = () => {
    if (typeof window.speechSynthesis === "undefined") {
      toast.error("SpeechSynthesis não disponível no navegador");
      return;
    }
    const tpl = TTS_LANGUAGES.find((l) => l.code === config.tts.lang)?.template || "Teste";
    const text = tpl
      .replace("{ticket}", "A-101")
      .replace("{desk}", config.desks[0] || "01")
      .replace("{nomenclature}", config.nomenclature || "Guichê");
    window.speechSynthesis.cancel();
    const voice = pickVoice(allVoices, {
      lang: config.tts.lang,
      gender: config.tts.gender,
      voiceURI: config.tts.voiceURI,
    });
    const u = new SpeechSynthesisUtterance(text);
    u.lang = config.tts.lang;
    u.rate = config.tts.rate;
    u.pitch = config.tts.pitch;
    u.volume = config.tts.volume;
    if (voice) u.voice = voice;
    window.speechSynthesis.speak(u);
  };

  const addSlide = () => {
    const url = newSlide.trim();
    if (!url) return;
    updateNested("media", { slides: [...config.media.slides, url] });
    setNewSlide("");
  };

  const removeSlide = (i) => {
    const slides = config.media.slides.filter((_, idx) => idx !== i);
    updateNested("media", { slides });
  };

  const onUploadFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const readers = files.map(
      (f) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(f);
        })
    );
    try {
      const datas = await Promise.all(readers);
      updateNested("media", { slides: [...config.media.slides, ...datas] });
      toast.success(`${files.length} imagem(ns) adicionada(s)`);
    } catch {
      toast.error("Falha ao carregar imagens");
    }
    e.target.value = "";
  };

  const onUploadMp4 = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.warning("Arquivos maiores que 8MB podem travar o localStorage. Considere usar URL externa.");
    }
    const reader = new FileReader();
    reader.onload = () => {
      updateNested("media", { mp4Url: reader.result });
      toast.success("Vídeo MP4 carregado");
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const addPrefix = () => {
    const p = newPrefix.trim().toUpperCase();
    if (!p || config.prefixes.includes(p)) return;
    update({ prefixes: [...config.prefixes, p] });
    setNewPrefix("");
  };

  const removePrefix = (p) => {
    update({ prefixes: config.prefixes.filter((x) => x !== p) });
  };

  const addDesk = () => {
    const d = newDesk.trim();
    if (!d || config.desks.includes(d)) return;
    update({ desks: [...config.desks, d] });
    setNewDesk("");
  };

  const removeDesk = (d) => {
    update({ desks: config.desks.filter((x) => x !== d) });
  };

  const resetDefaults = () => {
    if (!window.confirm("Restaurar todas as configurações de aparência ao padrão?")) return;
    setConfig(() => ({ ...DEFAULT_CONFIG, brandName: config.brandName }));
    toast.success("Configurações restauradas");
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border sticky top-0 bg-background z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-foreground" />
            <span className="font-heading font-bold tracking-tighter text-lg">
              {company?.name || "Painel Administrativo"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-none" onClick={() => window.open("/tv", "_blank")} data-testid="admin-open-tv-btn">
              <Tv className="w-4 h-4 mr-2" /> TV
            </Button>
            <Button variant="outline" size="sm" className="rounded-none" onClick={() => navigate("/operator")} data-testid="admin-operator-btn">
              <UserCog className="w-4 h-4 mr-2" /> Operador
            </Button>
            <Button variant="outline" size="sm" className="rounded-none" onClick={onLogout} data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-foreground/60 mb-3">Configurações</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter">Personalize seu painel</h1>
        </div>

        <Tabs defaultValue="layout" className="w-full">
          <TabsList className="rounded-none bg-transparent p-0 border-b border-border w-full justify-start h-auto mb-8 overflow-x-auto" data-testid="admin-tabs">
            {[
              ["layout", "Layout"],
              ["colors", "Cores"],
              ["media", "Mídia"],
              ["ticker", "Ticker"],
              ["queue", "Fila"],
              ["tts", "Voz (TTS)"],
              ["kiosk", "Kiosk 24/7"],
              ["users", "Usuários"],
              ["billing", "Assinatura"],
            ].map(([v, label]) => (
              <TabsTrigger
                key={v}
                value={v}
                className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=active]:shadow-none px-6 py-3 text-xs uppercase tracking-[0.2em]"
                data-testid={`admin-tab-${v}`}
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* LAYOUT */}
          <TabsContent value="layout" className="space-y-6">
            <div className="border border-border p-8">
              <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-2">Posicionamento da Mídia</div>
              <p className="text-sm text-foreground/60 mb-6 max-w-2xl">
                Escolha um preset ou crie um layout personalizado. Mudanças aparecem na TV em ~2s, sem refresh.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {[
                  [LAYOUT_MODES.MEDIA_RIGHT, "Senha · Mídia"],
                  [LAYOUT_MODES.PASSWORD_TOP, "Senha em cima"],
                  [LAYOUT_MODES.MEDIA_TOP, "Mídia em cima"],
                  [LAYOUT_MODES.FULLSCREEN_PASSWORD, "Só Senha"],
                  [LAYOUT_MODES.PIP_MEDIA_BR, "PiP — Mídia canto"],
                  [LAYOUT_MODES.CARDS, "Cards · Prioritário + Normais"],
                ].map(([val, label]) => {
                  const active = config.layout === val;
                  return (
                    <button
                      key={val}
                      onClick={() => update({ layout: val })}
                      className={`p-4 border transition-all text-left flex flex-col gap-2 ${
                        active ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground"
                      }`}
                      data-testid={`admin-layout-${val}`}
                    >
                      <LayoutMiniature mode={val} active={active} />
                      <div className="font-heading text-sm font-black tracking-tight leading-tight">{label}</div>
                    </button>
                  );
                })}

                {/* Custom layout button — gated by plan */}
                <button
                  onClick={() => planLimits.custom_layout && update({ layout: LAYOUT_MODES.CUSTOM })}
                  disabled={!planLimits.custom_layout}
                  className={`p-4 border transition-all text-left flex flex-col gap-2 relative ${
                    config.layout === LAYOUT_MODES.CUSTOM
                      ? "border-foreground bg-foreground text-background"
                      : planLimits.custom_layout
                      ? "border-dashed border-foreground/60 hover:bg-foreground/5"
                      : "border-dashed border-border opacity-60 cursor-not-allowed"
                  }`}
                  data-testid={`admin-layout-${LAYOUT_MODES.CUSTOM}`}
                  title={planLimits.custom_layout ? "" : "Disponível em planos Pro e Enterprise"}
                >
                  <LayoutMiniature mode={LAYOUT_MODES.CUSTOM} active={config.layout === LAYOUT_MODES.CUSTOM} />
                  <div className="font-heading text-sm font-black tracking-tight leading-tight">Personalizado</div>
                  {!planLimits.custom_layout && (
                    <div className="absolute top-2 right-2 text-[8px] uppercase tracking-[0.2em] px-1.5 py-0.5 bg-foreground text-background">
                      Pro
                    </div>
                  )}
                </button>
              </div>
            </div>

            {/* Custom layout panel — only visible when CUSTOM is selected */}
            {config.layout === LAYOUT_MODES.CUSTOM && planLimits.custom_layout && (
              <div className="border border-border p-8 space-y-6" data-testid="custom-layout-panel">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-2">Layout Personalizado</div>
                  <h3 className="font-heading text-2xl font-black tracking-tighter">Ajuste fino</h3>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-[0.25em]">Orientação</Label>
                  <div className="mt-3 grid grid-cols-3 gap-0 border border-border">
                    {[
                      { id: "horizontal", label: "Horizontal" },
                      { id: "vertical", label: "Vertical" },
                      { id: "pip", label: "PiP (sobreposto)" },
                    ].map((opt, i) => {
                      const cur = config.layoutCustom?.orientation || "horizontal";
                      const active = cur === opt.id;
                      return (
                        <button
                          key={opt.id}
                          onClick={() => updateNested("layoutCustom", { orientation: opt.id })}
                          className={`h-12 text-xs uppercase tracking-[0.2em] transition-colors ${
                            i > 0 ? "border-l border-border" : ""
                          } ${active ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}
                          data-testid={`admin-custom-orientation-${opt.id}`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {(config.layoutCustom?.orientation || "horizontal") !== "pip" && (
                  <>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.25em]">
                        Tamanho da Senha ({config.layoutCustom?.passwordRatio || 50}%)
                      </Label>
                      <Slider
                        value={[config.layoutCustom?.passwordRatio || 50]}
                        min={20}
                        max={80}
                        step={5}
                        onValueChange={(v) => updateNested("layoutCustom", { passwordRatio: v[0] })}
                        className="mt-3"
                        data-testid="admin-custom-ratio"
                      />
                      <p className="text-[10px] uppercase tracking-[0.2em] text-foreground/50 mt-2">
                        Quanto maior, mais espaço a senha ocupa em relação à mídia.
                      </p>
                    </div>

                    <div>
                      <Label className="text-xs uppercase tracking-[0.25em]">
                        Posição da Senha
                      </Label>
                      <div className="mt-3 grid grid-cols-2 gap-0 border border-border">
                        {((config.layoutCustom?.orientation || "horizontal") === "horizontal"
                          ? [{ id: "left", label: "Esquerda" }, { id: "right", label: "Direita" }]
                          : [{ id: "top", label: "Em cima" }, { id: "bottom", label: "Embaixo" }]
                        ).map((opt, i) => {
                          const cur = config.layoutCustom?.passwordPosition || "left";
                          const active = cur === opt.id;
                          return (
                            <button
                              key={opt.id}
                              onClick={() => updateNested("layoutCustom", { passwordPosition: opt.id })}
                              className={`h-12 text-xs uppercase tracking-[0.2em] transition-colors ${
                                i > 0 ? "border-l border-border" : ""
                              } ${active ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}
                              data-testid={`admin-custom-pos-${opt.id}`}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}

                {(config.layoutCustom?.orientation || "horizontal") === "pip" && (
                  <>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.25em]">Elemento Pequeno (PiP)</Label>
                      <div className="mt-3 grid grid-cols-2 gap-0 border border-border">
                        {[
                          { id: "media", label: "Mídia no canto" },
                          { id: "password", label: "Senha no canto" },
                        ].map((opt, i) => {
                          const cur = config.layoutCustom?.pipTarget || "media";
                          const active = cur === opt.id;
                          return (
                            <button
                              key={opt.id}
                              onClick={() => updateNested("layoutCustom", { pipTarget: opt.id })}
                              className={`h-12 text-xs uppercase tracking-[0.2em] transition-colors ${
                                i > 0 ? "border-l border-border" : ""
                              } ${active ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}
                              data-testid={`admin-custom-piptarget-${opt.id}`}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs uppercase tracking-[0.25em]">Canto do PiP</Label>
                      <div className="mt-3 grid grid-cols-4 gap-0 border border-border">
                        {[
                          { id: "tl", label: "↖ Sup. Esq." },
                          { id: "tr", label: "↗ Sup. Dir." },
                          { id: "bl", label: "↙ Inf. Esq." },
                          { id: "br", label: "↘ Inf. Dir." },
                        ].map((opt, i) => {
                          const cur = config.layoutCustom?.passwordPosition || "br";
                          const active = cur === opt.id;
                          return (
                            <button
                              key={opt.id}
                              onClick={() => updateNested("layoutCustom", { passwordPosition: opt.id })}
                              className={`h-12 text-xs uppercase tracking-[0.2em] transition-colors ${
                                i > 0 ? "border-l border-border" : ""
                              } ${active ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}
                              data-testid={`admin-custom-corner-${opt.id}`}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs uppercase tracking-[0.25em]">
                        Tamanho do PiP ({config.layoutCustom?.pipSize || 25}% da tela)
                      </Label>
                      <Slider
                        value={[config.layoutCustom?.pipSize || 25]}
                        min={10}
                        max={45}
                        step={5}
                        onValueChange={(v) => updateNested("layoutCustom", { pipSize: v[0] })}
                        className="mt-3"
                        data-testid="admin-custom-pipsize"
                      />
                    </div>
                  </>
                )}

                {/* Live preview */}
                <div>
                  <Label className="text-xs uppercase tracking-[0.25em] mb-3 block">Pré-visualização</Label>
                  <div className="aspect-video bg-muted/30 border border-border overflow-hidden">
                    <LayoutPreview customCfg={config.layoutCustom} mode={LAYOUT_MODES.CUSTOM} />
                  </div>
                </div>
              </div>
            )}

            <div className="border border-border p-8">
              <Label className="text-xs uppercase tracking-[0.25em]">Nome da Marca / Empresa</Label>
              <Input
                value={config.brandName}
                onChange={(e) => update({ brandName: e.target.value })}
                className="mt-3 h-12 rounded-none"
                data-testid="admin-brandname-input"
              />
            </div>
            <div className="border border-border p-8">
              <Label className="text-xs uppercase tracking-[0.25em]">Nomenclatura da Estação</Label>
              <Input
                value={config.nomenclature}
                onChange={(e) => update({ nomenclature: e.target.value })}
                placeholder="Guichê, Consultório, Mesa, Sala, Balcão..."
                className="mt-3 h-12 rounded-none"
                data-testid="admin-nomenclature-input"
              />
              <p className="text-xs text-foreground/60 mt-3">
                Substitui a palavra "Guichê" em toda a aplicação — TV, anúncio por voz e operador.
                Exemplos: <span className="font-mono">Consultório</span>, <span className="font-mono">Mesa</span>, <span className="font-mono">Sala</span>, <span className="font-mono">Balcão</span>.
              </p>
            </div>
          </TabsContent>

          {/* COLORS */}
          <TabsContent value="colors" className="space-y-6">
            <div className="border border-border p-8">
              <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-6">Fundos com Transparência & Imagem</div>
              <ZoneBgEditor label="Zona da Senha" prefix="password" colors={config.colors} onChange={(patch) => updateNested("colors", patch)} />
              <ZoneBgEditor label="Zona da Mídia" prefix="media" colors={config.colors} onChange={(patch) => updateNested("colors", patch)} />
              <ZoneBgEditor label="Ticker (rodapé)" prefix="ticker" colors={config.colors} onChange={(patch) => updateNested("colors", patch)} />
              <ZoneBgEditor label="Cards de Histórico (layout CARDS)" prefix="historyCard" colors={config.colors} onChange={(patch) => updateNested("colors", patch)} />
            </div>

            <div className="border border-border p-8">
              <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-6">Cores de Texto & Destaque</div>
              <ColorPickerField label="Texto Zona Senha" value={config.colors.passwordText} onChange={(v) => updateNested("colors", { passwordText: v })} testId="color-passwordText" />
              <ColorPickerField label="Acento Senha Atual (Prioritário)" value={config.colors.passwordAccent} onChange={(v) => updateNested("colors", { passwordAccent: v })} testId="color-passwordAccent" />
              <ColorPickerField label="Acento Histórico (Normal)" value={config.colors.historyAccent} onChange={(v) => updateNested("colors", { historyAccent: v })} testId="color-historyAccent" />
              <ColorPickerField label="Texto Ticker" value={config.colors.tickerText} onChange={(v) => updateNested("colors", { tickerText: v })} testId="color-tickerText" />
            </div>

            <div className="border border-border p-8">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Mostrar relógio no ticker</Label>
                  <p className="text-xs text-foreground/60 mt-1">Exibe data/hora no canto direito do rodapé.</p>
                </div>
                <Switch
                  checked={config.showClock !== false}
                  onCheckedChange={(v) => update({ showClock: v })}
                  data-testid="admin-show-clock-toggle"
                />
              </div>
            </div>

            <Button variant="outline" className="rounded-none" onClick={resetDefaults} data-testid="admin-reset-defaults-btn">
              <RotateCw className="w-4 h-4 mr-2" /> Restaurar Padrões
            </Button>
          </TabsContent>

          {/* MEDIA */}
          <TabsContent value="media" className="space-y-6">
            <div className="border border-border p-8">
              <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-6">Tipo de Mídia</div>
              <Select value={config.media.type} onValueChange={(v) => updateNested("media", { type: v })}>
                <SelectTrigger className="h-12 rounded-none" data-testid="admin-media-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={MEDIA_TYPES.PLAYLIST} data-testid="admin-media-playlist">Playlist Mista (recomendado)</SelectItem>
                  <SelectItem value={MEDIA_TYPES.SLIDES} data-testid="admin-media-slides">Apenas Slides de Imagens</SelectItem>
                  <SelectItem value={MEDIA_TYPES.YOUTUBE} data-testid="admin-media-youtube">Apenas YouTube</SelectItem>
                  <SelectItem value={MEDIA_TYPES.MP4} data-testid="admin-media-mp4">Apenas Vídeo MP4</SelectItem>
                  <SelectItem value={MEDIA_TYPES.RSS} data-testid="admin-media-rss">Apenas Feed RSS</SelectItem>
                  <SelectItem value={MEDIA_TYPES.NONE} data-testid="admin-media-none">Desativado</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-foreground/60 mt-3">
                Use <strong>Playlist Mista</strong> para reproduzir vários itens em sequência (imagens, YouTube, MP4 e RSS misturados).
              </p>

              <div className="mt-8 pt-6 border-t border-border">
                <div className="flex items-baseline justify-between mb-3">
                  <Label className="text-xs uppercase tracking-[0.25em]">
                    Volume da Mídia ({Math.round((config.media.volume ?? 1) * 100)}%)
                  </Label>
                  <span className="text-[10px] uppercase tracking-[0.25em] text-foreground/50">
                    Aplica-se a YouTube e MP4
                  </span>
                </div>
                <Slider
                  value={[config.media.volume ?? 1]}
                  min={0}
                  max={1}
                  step={0.05}
                  onValueChange={(v) => updateNested("media", { volume: v[0] })}
                  data-testid="admin-media-volume"
                />
                <p className="text-[10px] uppercase tracking-[0.25em] text-foreground/50 mt-3">
                  A mídia muta automaticamente durante a chamada da senha e volta ao volume escolhido em seguida.
                </p>
              </div>
            </div>

            {config.media.type === MEDIA_TYPES.PLAYLIST && (
              <PlaylistEditor
                playlist={config.media.playlist || []}
                shuffle={!!config.media.playlistShuffle}
                onChange={(patch) => updateNested("media", patch)}
              />
            )}

            {config.media.type === MEDIA_TYPES.SLIDES && (
              <div className="border border-border p-8 space-y-4">
                <Label className="text-xs uppercase tracking-[0.25em]">Imagens (URL ou upload)</Label>
                <div className="flex gap-2">
                  <Input
                    value={newSlide}
                    onChange={(e) => setNewSlide(e.target.value)}
                    placeholder="https://..."
                    className="h-11 rounded-none"
                    data-testid="admin-slide-url-input"
                  />
                  <Button onClick={addSlide} className="rounded-none" data-testid="admin-slide-add-btn">
                    <Plus className="w-4 h-4 mr-2" /> Adicionar
                  </Button>
                </div>
                <div>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={onUploadFile}
                    className="block text-sm file:mr-4 file:py-2 file:px-4 file:border file:border-border file:bg-background file:text-foreground file:rounded-none hover:file:bg-muted"
                    data-testid="admin-slide-upload-input"
                  />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                  {config.media.slides.map((s, i) => (
                    <div key={i} className="relative border border-border group">
                      <img src={s} alt={`slide-${i}`} className="w-full h-32 object-cover" />
                      <button
                        onClick={() => removeSlide(i)}
                        className="absolute top-2 right-2 bg-foreground text-background p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`admin-slide-remove-${i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="pt-4 border-t border-border">
                  <Label className="text-xs uppercase tracking-[0.25em]">Intervalo (segundos)</Label>
                  <div className="flex items-center gap-4 mt-3">
                    <Slider
                      value={[config.media.slideInterval]}
                      min={3}
                      max={30}
                      step={1}
                      onValueChange={(v) => updateNested("media", { slideInterval: v[0] })}
                      className="flex-1"
                      data-testid="admin-slide-interval-slider"
                    />
                    <span className="font-mono text-sm w-12 text-right">{config.media.slideInterval}s</span>
                  </div>
                </div>
              </div>
            )}

            {config.media.type === MEDIA_TYPES.YOUTUBE && (
              <div className="border border-border p-8 space-y-3">
                <Label className="text-xs uppercase tracking-[0.25em]">URL do YouTube</Label>
                <Input
                  value={config.media.youtubeUrl}
                  onChange={(e) => updateNested("media", { youtubeUrl: e.target.value })}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="h-12 rounded-none"
                  data-testid="admin-youtube-url-input"
                />
                <p className="text-xs text-foreground/60">Cole a URL completa. Será convertida automaticamente para player embedável com autoplay.</p>
              </div>
            )}

            {config.media.type === MEDIA_TYPES.MP4 && (
              <div className="border border-border p-8 space-y-4">
                <Label className="text-xs uppercase tracking-[0.25em]">Vídeo MP4 (URL ou upload)</Label>
                <Input
                  value={config.media.mp4Url?.startsWith("data:") ? "[Upload ativo]" : config.media.mp4Url}
                  onChange={(e) => updateNested("media", { mp4Url: e.target.value })}
                  placeholder="https://... .mp4"
                  className="h-12 rounded-none"
                  data-testid="admin-mp4-url-input"
                />
                <input
                  type="file"
                  accept="video/mp4"
                  onChange={onUploadMp4}
                  className="block text-sm file:mr-4 file:py-2 file:px-4 file:border file:border-border file:bg-background file:text-foreground file:rounded-none hover:file:bg-muted"
                  data-testid="admin-mp4-upload-input"
                />
                <p className="text-xs text-foreground/60">Recomendado: usar URL externa para vídeos &gt; 5MB.</p>
              </div>
            )}

            {config.media.type === MEDIA_TYPES.RSS && (
              <div className="border border-border p-8 space-y-3">
                <Label className="text-xs uppercase tracking-[0.25em]">URL do Feed RSS</Label>
                <Input
                  value={config.media.rssUrl}
                  onChange={(e) => updateNested("media", { rssUrl: e.target.value })}
                  placeholder="https://exemplo.com/feed.xml"
                  className="h-12 rounded-none"
                  data-testid="admin-rss-url-input"
                />
                <p className="text-xs text-foreground/60">As notícias são carregadas via rss2json.com (CORS-friendly).</p>
              </div>
            )}
          </TabsContent>

          {/* TICKER */}
          <TabsContent value="ticker" className="space-y-6">
            <div className="border border-border p-8">
              <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-4">
                Mensagens do Letreiro (uma por linha)
              </div>
              <Textarea
                value={config.ticker.join("\n")}
                onChange={(e) => update({ ticker: e.target.value.split("\n").filter((l) => l.trim() !== "") })}
                rows={8}
                className="rounded-none font-mono text-sm"
                data-testid="admin-ticker-textarea"
              />
            </div>
          </TabsContent>

          {/* QUEUE */}
          <TabsContent value="queue" className="space-y-6">
            <div className="border border-border p-8">
              <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-4">Prefixos de Senha</div>
              <div className="flex flex-wrap gap-2 mb-4">
                {config.prefixes.map((p) => (
                  <span key={p} className="inline-flex items-center gap-2 border border-border px-3 py-1 font-mono text-sm">
                    {p}
                    <button onClick={() => removePrefix(p)} className="text-foreground/60 hover:text-destructive" data-testid={`admin-prefix-remove-${p}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newPrefix} onChange={(e) => setNewPrefix(e.target.value)} placeholder="Ex: E" className="h-11 rounded-none w-40" data-testid="admin-new-prefix-input" />
                <Button onClick={addPrefix} className="rounded-none" data-testid="admin-add-prefix-btn">
                  <Plus className="w-4 h-4 mr-2" /> Adicionar
                </Button>
              </div>
            </div>

            <div className="border border-border p-8">
              <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-4">
                {config.nomenclature || "Guichê"}s Disponíveis
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {config.desks.map((d) => (
                  <span key={d} className="inline-flex items-center gap-2 border border-border px-3 py-1 font-mono text-sm">
                    {d}
                    <button onClick={() => removeDesk(d)} className="text-foreground/60 hover:text-destructive" data-testid={`admin-desk-remove-${d}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newDesk} onChange={(e) => setNewDesk(e.target.value)} placeholder="Ex: 07" className="h-11 rounded-none w-40" data-testid="admin-new-desk-input" />
                <Button onClick={addDesk} className="rounded-none" data-testid="admin-add-desk-btn">
                  <Plus className="w-4 h-4 mr-2" /> Adicionar
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* TTS */}
          <TabsContent value="tts" className="space-y-6">
            <div className="border border-border p-8 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-1">Anúncio por Voz</div>
                  <div className="font-heading text-2xl font-bold tracking-tight">SpeechSynthesis</div>
                </div>
                <Switch
                  checked={config.tts.enabled}
                  onCheckedChange={(v) => updateNested("tts", { enabled: v })}
                  data-testid="admin-tts-toggle"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.25em]">Idioma</Label>
                <Select value={config.tts.lang} onValueChange={(v) => updateNested("tts", { lang: v, voiceURI: "" })}>
                  <SelectTrigger className="mt-3 h-12 rounded-none" data-testid="admin-tts-lang-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TTS_LANGUAGES.map((l) => (
                      <SelectItem key={l.code} value={l.code} data-testid={`admin-tts-lang-${l.code}`}>
                        {l.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-xs uppercase tracking-[0.25em]">Gênero da voz</Label>
                  <div className="mt-3 grid grid-cols-3 gap-0 border border-border">
                    {[
                      { id: "auto", label: "Padrão" },
                      { id: "female", label: "Feminina ♀" },
                      { id: "male", label: "Masculina ♂" },
                    ].map((opt, i) => {
                      const current = config.tts.gender || "auto";
                      const active = current === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => updateNested("tts", { gender: opt.id, voiceURI: "" })}
                          className={`h-12 text-xs uppercase tracking-[0.2em] transition-colors ${
                            i > 0 ? "border-l border-border" : ""
                          } ${active ? "bg-foreground text-background" : "bg-background hover:bg-muted"}`}
                          data-testid={`admin-tts-gender-${opt.id}`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-foreground/50 mt-2">
                    "Padrão" usa a voz padrão do navegador para o idioma.
                  </p>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-[0.25em]">
                    Voz específica (opcional)
                  </Label>
                  <Select
                    value={config.tts.voiceURI || "auto"}
                    onValueChange={(v) => updateNested("tts", { voiceURI: v === "auto" ? "" : v })}
                  >
                    <SelectTrigger className="mt-3 h-12 rounded-none" data-testid="admin-tts-voice-select">
                      <SelectValue placeholder="Auto (segue o gênero acima)" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="auto" data-testid="admin-tts-voice-auto">
                        Auto (segue o gênero acima)
                      </SelectItem>
                      {listVoicesForUI(allVoices, config.tts.lang).map((v) => (
                        <SelectItem key={v.voiceURI} value={v.voiceURI} data-testid={`admin-tts-voice-${v.voiceURI}`}>
                          {v.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-foreground/50 mt-2">
                    {allVoices.length === 0
                      ? "Aguardando vozes do navegador…"
                      : `${listVoicesForUI(allVoices, config.tts.lang).length} voz(es) disponível(is) para ${config.tts.lang}`}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <Label className="text-xs uppercase tracking-[0.25em]">Velocidade ({config.tts.rate.toFixed(2)})</Label>
                  <Slider value={[config.tts.rate]} min={0.5} max={1.5} step={0.05} onValueChange={(v) => updateNested("tts", { rate: v[0] })} className="mt-3" data-testid="admin-tts-rate" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.25em]">Tom ({config.tts.pitch.toFixed(2)})</Label>
                  <Slider value={[config.tts.pitch]} min={0.5} max={1.5} step={0.05} onValueChange={(v) => updateNested("tts", { pitch: v[0] })} className="mt-3" data-testid="admin-tts-pitch" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.25em]">Volume da Voz ({Math.round(config.tts.volume * 100)}%)</Label>
                  <Slider value={[config.tts.volume]} min={0} max={1} step={0.05} onValueChange={(v) => updateNested("tts", { volume: v[0] })} className="mt-3" data-testid="admin-tts-volume" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.25em]">Repetir ({config.tts.repeat}x)</Label>
                  <Slider value={[config.tts.repeat]} min={1} max={3} step={1} onValueChange={(v) => updateNested("tts", { repeat: v[0] })} className="mt-3" data-testid="admin-tts-repeat" />
                </div>
              </div>

              <Button onClick={testTTS} className="rounded-none" data-testid="admin-tts-test-btn">
                <Volume2 className="w-4 h-4 mr-2" /> Testar Voz
              </Button>
            </div>
          </TabsContent>

          {/* KIOSK 24/7 */}
          <TabsContent value="kiosk" className="space-y-6">
            <div className="border border-border p-8 space-y-6">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-2">Operação Não Atendida</div>
                <h2 className="font-heading text-2xl font-black tracking-tighter">Modo Kiosk 24/7</h2>
                <p className="text-sm text-foreground/60 mt-2 max-w-2xl">
                  Configure aqui o comportamento da TV quando ela fica ligada o tempo todo
                  sem operador para clicar em nada. Ajustes recomendados para clínicas e
                  recepções abertas 24 horas.
                </p>
              </div>

              <div className="border-t border-border pt-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center">
                <div>
                  <Label className="text-sm font-semibold uppercase tracking-[0.2em]">Manter tela ligada (Wake Lock)</Label>
                  <p className="text-xs text-foreground/60 mt-1 max-w-xl">
                    Impede o sistema operacional de apagar a tela ou entrar em modo de suspensão.
                    Funciona no Chrome/Edge (Windows, Mac, Android). No Linux/Safari antigo, configure
                    também o "screen saver" do sistema.
                  </p>
                </div>
                <Switch
                  checked={config.kiosk?.wakeLock !== false}
                  onCheckedChange={(v) => updateNested("kiosk", { wakeLock: v })}
                  data-testid="admin-kiosk-wake-lock"
                />
              </div>

              <div className="border-t border-border pt-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center">
                <div>
                  <Label className="text-sm font-semibold uppercase tracking-[0.2em]">Modo silencioso por padrão</Label>
                  <p className="text-xs text-foreground/60 mt-1 max-w-xl">
                    Pula o modal "Ativar som?" sempre que a TV recarregar. A mídia roda muda
                    automaticamente — só a chamada por voz (TTS) toca. Ideal quando o ambiente
                    deve ficar silencioso ou ninguém vai estar lá para clicar.
                  </p>
                </div>
                <Switch
                  checked={!!config.kiosk?.silentByDefault}
                  onCheckedChange={(v) => updateNested("kiosk", { silentByDefault: v })}
                  data-testid="admin-kiosk-silent-default"
                />
              </div>

              <div className="border-t border-border pt-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center">
                <div>
                  <Label className="text-sm font-semibold uppercase tracking-[0.2em]">Indicador de conexão</Label>
                  <p className="text-xs text-foreground/60 mt-1 max-w-xl">
                    Mostra um pontinho discreto no canto superior esquerdo da TV: verde piscando
                    quando está conectada ao servidor, vermelho se ficar mais de 8 segundos sem resposta.
                    Permite identificar de longe se algo travou.
                  </p>
                </div>
                <Switch
                  checked={config.kiosk?.heartbeat !== false}
                  onCheckedChange={(v) => updateNested("kiosk", { heartbeat: v })}
                  data-testid="admin-kiosk-heartbeat"
                />
              </div>

              <div className="border-t border-border pt-6 space-y-3">
                <div>
                  <Label className="text-sm font-semibold uppercase tracking-[0.2em]">Recarregar TV diariamente</Label>
                  <p className="text-xs text-foreground/60 mt-1 max-w-xl">
                    Reinicia o navegador automaticamente todo dia no horário escolhido.
                    Recomendado às 03:00 ou 04:00 (movimento mínimo) para evitar acúmulo de
                    memória após dias de uso ininterrupto. Deixe vazio para desativar.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Input
                    type="time"
                    value={config.kiosk?.autoReloadTime || ""}
                    onChange={(e) => updateNested("kiosk", { autoReloadTime: e.target.value })}
                    className="h-11 rounded-none w-40 font-mono"
                    data-testid="admin-kiosk-reload-time"
                  />
                  {config.kiosk?.autoReloadTime && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-none"
                      onClick={() => updateNested("kiosk", { autoReloadTime: "" })}
                      data-testid="admin-kiosk-reload-clear"
                    >
                      Desativar
                    </Button>
                  )}
                </div>
              </div>

              <div className="border-t border-border pt-6">
                <Label className="text-sm font-semibold uppercase tracking-[0.2em]">Guia de configuração do sistema operacional</Label>
                <p className="text-xs text-foreground/60 mt-2 max-w-2xl">
                  Para uma operação realmente 24/7 sem intervenção, configure também:
                </p>
                <ul className="text-xs text-foreground/70 mt-3 space-y-1.5 list-disc list-inside max-w-2xl">
                  <li><strong>Chrome em modo Kiosk</strong>: atalho com <code className="font-mono bg-muted px-1">--kiosk --app=https://chama.licensehub.com.br/tv</code></li>
                  <li><strong>Desativar protetor de tela / suspensão</strong> no Windows: Configurações → Sistema → Energia</li>
                  <li><strong>Auto-login do Windows</strong>: <code className="font-mono bg-muted px-1">netplwiz</code> e desmarque "Usuários devem digitar senha"</li>
                  <li><strong>Iniciar Chrome no boot</strong>: coloque o atalho em <code className="font-mono bg-muted px-1">shell:startup</code></li>
                  <li><strong>UPS / nobreak</strong>: para sobreviver a quedas curtas de energia sem reiniciar</li>
                </ul>
              </div>
            </div>
          </TabsContent>

          {/* USERS */}
          <TabsContent value="users" className="space-y-6">
            <CompanyUsers />
          </TabsContent>

          {/* BILLING */}
          <TabsContent value="billing" className="space-y-6">
            <SubscriptionTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Admin;
